import type { StatusIconBarItem } from '@chatsdet/ui';
import type { RunTriageLockupCounts } from '../triage/RunTriageLockup';
import type { RunSummaryData } from './useRunSummaryData';

type RunTotals = {
  passed: number;
  failed: number;
  skipped: number;
  degraded: number;
  settled: number;
  passRate: number;
};

/**
 * Shared pass-rate/count rule for a run's `StatusIconBarItem[]`. Previously
 * duplicated between `RunSummary`'s full variant and `LiveRunCard` with
 * comments begging them to stay in lockstep — extracted here so there is one
 * source of truth.
 *
 * - `runnable` excludes not-run items (legacy quarantined + `degraded`) from
 *   the pass-rate denominator.
 * - `passed` counts `success` + `flaky_success`; `failed` counts `failed`,
 *   both computed off `runnable`.
 * - `settled = passed + failed`; `passRate = settled > 0 ? round(passed /
 *   settled * 100) : 0`.
 * - `skipped` and `degraded` are computed off the RAW `items` array (not
 *   `runnable`) — intentionally different from `passed`/`failed`, matching
 *   what `RunSummary` has always done.
 */
export function computeRunTotals(items: StatusIconBarItem[]): RunTotals {
  const runnable = items.filter((item) => !item.isQuarantined && item.status !== 'degraded');
  const passed = runnable.filter(
    (item) => item.status === 'success' || item.status === 'flaky_success'
  ).length;
  const failed = runnable.filter((item) => item.status === 'failed').length;
  const settled = passed + failed;
  const passRate = settled > 0 ? Math.round((passed / settled) * 100) : 0;
  const skipped = items.filter((item) => item.status === 'skipped').length;
  const degraded = items.filter((item) => item.status === 'degraded').length;

  return { passed, failed, skipped, degraded, settled, passRate };
}

/**
 * `RunTriageLockup`'s counts for a native suite run. When `items` is present
 * (the common case), derive them from `computeRunTotals`. When it's empty but
 * the execution still reports real totals (`detailRowsUnavailable` — the API
 * sent execution-level counts without per-workflow rows), fall back to those
 * execution fields instead of `computeRunTotals([])`, which would read as a
 * run with nothing in it rather than a run whose detail rows are just
 * unavailable.
 */
export function runSummaryLockupCounts(data: RunSummaryData): RunTriageLockupCounts {
  if (data.items.length > 0) {
    const { passed, failed, skipped, degraded } = computeRunTotals(data.items);
    return { passed, failed, skipped, degraded, completed: data.completedWorkflows, total: data.totalWorkflows };
  }
  return {
    passed: data.successfulWorkflows,
    failed: data.failedWorkflows,
    skipped: data.skippedWorkflows,
    degraded: data.degradedWorkflows,
    completed: data.completedWorkflows,
    total: data.totalWorkflows,
  };
}
